import React, { useEffect, useState, useMemo } from 'react';
import { Trash2, Search } from 'lucide-react';
import {
  getInactiveEmployees,
  changeUserStatus,
  deleteEmployee,
} from '../../../services/admin/api';
import { toast } from 'react-toastify';
import './employee.css';

const InactiveEmployeeList = () => {
  const [employees, setEmployees] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 7;

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const data = await getInactiveEmployees();
      setEmployees(data || []);
    } catch (error) {
      console.error('Error fetching inactive employees:', error);
      setEmployees([]);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortOrder('asc');
    }
  };

  const filteredEmployees = useMemo(() => {
    return employees
      .filter((employee) =>
        employee.name?.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .sort((a, b) => {
        if (a[sortColumn] < b[sortColumn]) return sortOrder === 'asc' ? -1 : 1;
        if (a[sortColumn] > b[sortColumn]) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      });
  }, [employees, searchTerm, sortColumn, sortOrder]);

  const paginatedEmployees = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredEmployees.slice(start, start + itemsPerPage);
  }, [filteredEmployees, currentPage]);

  const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);
  const goToPage = (page) => setCurrentPage(page);

  const handleDelete = (employee) => {
    setSelectedEmployee(employee);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteEmployee(selectedEmployee.id);
      setShowDeleteModal(false);
      setSelectedEmployee(null);
      setFeedbackMessage('Employee deleted successfully.');
      setShowFeedbackModal(true);
      fetchEmployees();
    } catch (error) {
      console.error('Error deleting employee:', error);
      setShowDeleteModal(false);
      setSelectedEmployee(null);
      setFeedbackMessage('Failed to delete employee.');
      setShowFeedbackModal(true);
    }
  };

  return (
    <div className="admin-employee-table-wrapper">
      {/* 🔍 Search Bar */}
      <div className="admin-search-bar-container">
        <div className="admin-search-input-wrapper">
          <input
            type="text"
            placeholder="Search employee by name..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="admin-search-input"
          />
          <button
            type="button"
            className="admin-search-icon-btn"
            onClick={() => {
              setSearchTerm(searchText);
              setCurrentPage(1);
            }}
          >
            <Search size={18} />
          </button>
        </div>
      </div>

      {/* 🧾 Table */}
      <table className="admin-employee-table">
        <thead>
          <tr>
            <th onClick={() => handleSort('name')}>Name</th>
            <th onClick={() => handleSort('email')}>Email</th>
            <th onClick={() => handleSort('contact')}>Contact Number</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {paginatedEmployees.length === 0 ? (
            <tr>
              <td colSpan="5" style={{ textAlign: 'center' }}>
                No inactive employees found.
              </td>
            </tr>
          ) : (
            paginatedEmployees.map((employee) => (
              <tr key={employee.id}>
                <td>{employee.name}</td>
                <td>{employee.email}</td>
                <td>{employee.contact}</td>
                <td className="admin-icon-cell">
                  <div className="admin-employee-action-buttons">
                    <button
                      title="Delete"
                      onClick={() => handleDelete(employee)}
                      className="admin-action-btn delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {/* 📄 Pagination */}
      <div className="admin-pagination-controls">
        <button onClick={() => goToPage(Math.max(currentPage - 1, 1))} disabled={currentPage === 1}>
          Prev
        </button>
        <span>
          Page {currentPage} of {totalPages}
        </span>
        <button onClick={() => goToPage(Math.min(currentPage + 1, totalPages))} disabled={currentPage === totalPages}>
          Next
        </button>
      </div>

      {/* ⚠️ Delete Confirmation Modal */}
      {showDeleteModal && selectedEmployee && (
        <div className="admin-modal-overlay">
          <div className="admin-modal-content">
            <h3>Confirm Deletion</h3>
            <p>
              Are you sure you want to permanently delete{' '}
              <span style={{ color: '#ff5252' }}>{selectedEmployee.name}</span>?
            </p>
            <div className="admin-modal-buttons">
              <button className="admin-assign-confirm" onClick={confirmDelete}>
                Delete
              </button>
              <button
                className="admin-assign-cancel"
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedEmployee(null);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ Feedback Modal */}
      {showFeedbackModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal-content">
            <p>{feedbackMessage}</p>
            <div className="admin-popup-modal-buttons">
              <button
                className="admin-popup-ok-btn"
                onClick={() => setShowFeedbackModal(false)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InactiveEmployeeList;
